import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useAutocompleteProductsQuery } from "../hooks/useAutocompleteProductsQuery";
import {
  addProductIds,
  mergeProductLabels,
  missingProductIds,
  type ProductOption,
} from "../model/productLabels";
import { ProductLabelsContext } from "../model/productLabelsContext";

/**
 * Общие названия выбранных продуктов для всех полей ProductsAutocomplete
 * внутри (супер-ревью кабинета 9): редактор с десятком строк условий
 * спрашивает названия одним запросом autocompleteProducts(value:) на все
 * id, которых ещё никто не знает, а не запросом на строку. Выбор продукта
 * из списка запроса не даёт: его название уже пришло вариантом.
 */
export const ProductLabelsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [requested, setRequested] = useState<readonly string[]>([]);
  const [known, setKnown] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const missing = missingProductIds(requested, known);
  const { data } = useAutocompleteProductsQuery({
    variables: { value: missing },
    skip: missing.length === 0,
    fetchPolicy: "cache-first",
  });
  // Ответ переносится в known при рендере (с охраной: только если пришло
  // новое), после чего missing пуст и запрос снова пропускается.
  const merged = mergeProductLabels(
    known,
    data?.autocompleteProducts?.value ?? []
  );
  if (merged !== known) setKnown(merged);

  const request = useCallback(
    (ids: readonly string[]) =>
      setRequested((prev) => addProductIds(prev, ids)),
    []
  );
  const remember = useCallback(
    (options: readonly ProductOption[]) =>
      setKnown((prev) => mergeProductLabels(prev, options)),
    []
  );
  const value = useMemo(
    () => ({ labels: known, request, remember }),
    [known, request, remember]
  );
  return (
    <ProductLabelsContext.Provider value={value}>
      {children}
    </ProductLabelsContext.Provider>
  );
};
